import React from "react";
import { PageHead } from "@/components/PageHead";
import { Seo } from "@/components/Seo";
import { CONCEPTS } from "@/content/concepts";
import type { LanguageBinding } from "@/hooks/useLanguage";
import { cx } from "@/lib/classnames";

export const Concepts: React.FC<{ language: LanguageBinding }> = ({ language }) => {
  const { t, lang, isRTL } = language;
  return (
    <>
      <Seo
        title={t.meta.concepts.title}
        description={t.meta.concepts.description}
        lang={lang}
        path="/concepts"
      />
      <PageHead
        kicker={t.conceptsPage.kicker}
        title={t.conceptsPage.title}
        tagline={t.conceptsPage.intro}
        isRTL={isRTL}
      />

      <section className="relative parchment-deckle">
        <div className="grain-soft" />
        <div className="max-w-page mx-auto px-6 md:px-10 py-20 md:py-24 relative">
          <div
            className="p-5 mb-12 inline-flex items-start gap-4 max-w-2xl"
            style={{
              border: "1px solid var(--hairline-light)",
              background: "var(--card-surface-warm)",
            }}
          >
            <svg
              width="18"
              height="18"
              viewBox="0 0 18 18"
              fill="none"
              stroke="var(--vermillion)"
              strokeWidth="1.4"
              className="mt-1 shrink-0"
              aria-hidden
            >
              <circle cx="9" cy="9" r="7.5" />
              <path d="M9 5v5M9 12v.5" strokeLinecap="round" />
            </svg>
            <div>
              <span
                className={cx(isRTL ? "ff-ar-sans" : "ff-en-sc tracked-wide", "text-[11px]")}
                style={{ color: "var(--vermillion)" }}
              >
                {t.conceptsPage.noteLabel}
              </span>
              <span
                className={cx(isRTL ? "ff-ar-sans" : "ff-en-body italic", "ms-2 text-sm")}
                style={{ color: "var(--muted-light)" }}
              >
                {t.conceptsPage.note}
              </span>
            </div>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-x-10 gap-y-12">
            {CONCEPTS.map((c, i) => (
              <article key={i} className="group">
                <div className="flex items-baseline gap-3 mb-3">
                  <span className="cat-num text-lg" style={{ color: "var(--vermillion)" }}>
                    {String(i + 1).padStart(2, "0")}
                  </span>
                  <span
                    className="flex-1 h-px"
                    style={{ background: "var(--hairline-light)" }}
                  />
                </div>
                <h4
                  className={cx(
                    isRTL ? "ff-ar-serif" : "ff-en-serif",
                    "text-2xl leading-snug mb-2",
                  )}
                  style={{ color: "var(--ink)", fontWeight: 600 }}
                >
                  {c[lang].t}
                </h4>
                <p
                  className={cx(isRTL ? "ff-ar-sans" : "ff-en-body", "leading-relaxed")}
                  style={{ color: "var(--muted-light)" }}
                >
                  {c[lang].b}
                </p>
                <div
                  className={cx(
                    isRTL ? "ff-ar-sans" : "ff-en-sc tracked-wide",
                    "text-[10px] mt-4 opacity-60",
                  )}
                  style={{ color: "var(--muted-light)" }}
                >
                  {t.conceptsPage.expandedTBA}
                </div>
              </article>
            ))}
          </div>
        </div>
      </section>
    </>
  );
};
